<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { fade } from 'svelte/transition';

    import { CLOSE_ICON, COUNTRY_FLAG_IMAGE } from './constants/loginImage';
    import { getFifaLoginFlowContext } from './context';
    import {
        loginPhoneNumber,
        markCodeSent,
        setLoginFlowLoading
    } from './store/loginFlow';
    import Toast from './Toast.svelte';
    import { handleKeyboardActivate } from './utils/a11y';
    import { checkPhoneApi, getPassportError } from './utils/loginPassport';
    import { getDurationSince, getErrorTrackAttrs } from './utils/trackHelper';

    import webp from '@/actions/webp';
    import url from '@/utils/url';

    export let isOpen = false;

    const { bids, trackEvent, phoneCellStore } = getFifaLoginFlowContext();
    const MAX_LENGTH = url.get('is_test') ? 11 : 10;

    const dispatch = createEventDispatcher<{
        close: void;
        confirm: void;
    }>();

    let toastRefs: Toast | null = null;
    let phoneInputNumber = '';
    let phoneInputRef: HTMLInputElement | null = null;
    let isInputError = false;
    let tipsMessage = '';
    let popupEnterTime = 0;
    let hasReportedTyp = false;

    $: if (isOpen) {
        document.body.style.overflow = 'hidden';
        formatPhoneInput($loginPhoneNumber);
        popupEnterTime = window.performance.now();
        hasReportedTyp = false;
        trackEvent(bids.changePhonePopupLoadingTimeSw, { duration: 200 });
        trackEvent(bids.changePhonePopupSw);
    }

    const showToast = (message: string) => {
        toastRefs?.show(message, { type: 'error' });
    };

    const reportPopupClose = () => {
        trackEvent(bids.changePhonePopupEx);
        trackEvent(bids.changePhonePopupTimeSw, {
            duration: getDurationSince(popupEnterTime)
        });
    };

    const closePopup = () => {
        document.body.style.overflow = '';
        reportPopupClose();
        dispatch('close');
    };

    const formatPhone = (digits: string) =>
        [digits.slice(0, 2), digits.slice(2, 6), digits.slice(6, MAX_LENGTH)]
            .filter(Boolean)
            .join(' ');

    const formatPhoneInput = (value: string) => {
        const digits = value.replace(/\D/g, '').slice(0, MAX_LENGTH);

        phoneInputNumber = digits;

        if (phoneInputRef) {
            phoneInputRef.value = formatPhone(digits);
        }
    };

    const handleConfirm = () => {
        trackEvent(bids.changePhonePopupCk, { button_name: 'next' });

        if (phoneInputNumber.length < MAX_LENGTH) {
            isInputError = true;
            tipsMessage = 'Por favor, ingresa tu número de celular completo';
            trackEvent(bids.changePhonePopupPhoneBt, {
                is_phone_bt_success: 0,
                error_code: 'phone_incomplete'
            });
            trackEvent(bids.changePhonePopupJumpBt, {
                is_jump_success: 0,
                error_code: 'phone_incomplete'
            });

            return;
        }

        if (phoneInputNumber === $loginPhoneNumber) {
            isInputError = true;
            tipsMessage = 'El numero ingresado es igual al existente. Ingrésalo de nuevo';
            trackEvent(bids.changePhonePopupPhoneBt, {
                is_phone_bt_success: 0,
                error_code: 'phone_same'
            });
            trackEvent(bids.changePhonePopupJumpBt, {
                is_jump_success: 0,
                error_code: 'phone_same'
            });

            return;
        }

        setLoginFlowLoading(true);

        checkPhoneApi(phoneInputNumber)
            .then(() => {
                trackEvent(bids.changePhonePopupPhoneBt, {
                    is_phone_bt_success: 1
                });
                trackEvent(bids.changePhonePopupJumpBt, { is_jump_success: 1 });
                markCodeSent();
                loginPhoneNumber.set(phoneInputNumber);
                phoneCellStore.set(phoneInputNumber);
                document.body.style.overflow = '';
                reportPopupClose();
                dispatch('confirm');
            })
            .catch((err) => {
                const response = getPassportError(err);
                const errorAttrs = getErrorTrackAttrs(response);

                trackEvent(bids.changePhonePopupPhoneBt, {
                    is_phone_bt_success: 0,
                    ...errorAttrs
                });
                trackEvent(bids.changePhonePopupJumpBt, {
                    is_jump_success: 0,
                    ...errorAttrs
                });

                if (response?.errno === 40001) {
                    isInputError = true;
                    tipsMessage = 'Número de celular inválido. Intenta de nuevo.';
                } else if (response?.errno === 53001) {
                    showToast(
                        'Demasiados intentos. Espera un poco antes de volver a intentar.'
                    );
                } else {
                    showToast(
                        'Error al obtener el código de verificación. Inténtalo de nuevo más tarde.'
                    );
                }
            })
            .finally(() => {
                setLoginFlowLoading(false);
            });
    };

    const handlePhoneInputClick = () => {
        trackEvent(bids.changePhonePopupCk, { button_name: 'input_box' });
    };

    const handlePhoneInput = (event: Event) => {
        if (!hasReportedTyp && phoneInputNumber.length === 0) {
            trackEvent(bids.changePhonePopupTyp);
            hasReportedTyp = true;
        }

        formatPhoneInput((event.target as HTMLInputElement).value);
        isInputError = false;
        tipsMessage = '';
    };

</script>

{#if isOpen}
    <div class="phone-change-popup" transition:fade={{ duration: 200 }}>
        <div class="popup-container" role="presentation">
            <div class="popup-close">
                <div
                    role="button"
                    tabindex="0"
                    class="close-icon"
                    use:webp={CLOSE_ICON}
                    on:click={closePopup}
                    on:keydown={(event) => handleKeyboardActivate(event, closePopup)}
                ></div>
            </div>
            <div class="popup-title">¿Ese no es tu celular?</div>
            <div class="popup-description">
                Actualiza tu número para enviarte tu código de nuevo.
            </div>
            <div class="phone-input-area" class:is-input-error={isInputError}>
                <div class="country-flag" use:webp={COUNTRY_FLAG_IMAGE}></div>
                <div class="calling-code">+52</div>
                <input
                    bind:this={phoneInputRef}
                    class="phone-input"
                    type="tel"
                    placeholder="55 1234 5678"
                    value={phoneInputNumber}
                    on:click={handlePhoneInputClick}
                    on:input={handlePhoneInput}
                />
            </div>
            {#if tipsMessage}
                <div class="tips-message">{tipsMessage}</div>
            {/if}
            <div
                class="confirm-btn"
                class:disabled={phoneInputNumber.length < MAX_LENGTH}
                role="button"
                tabindex="0"
                on:click={handleConfirm}
                on:keydown={(event) => handleKeyboardActivate(event, handleConfirm)}
            >
                Actualizar número
            </div>
        </div>
    </div>
{/if}

<Toast bind:this={toastRefs} />

<style lang="scss">
    .phone-change-popup {
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.6);
        z-index: 1000;
        display: flex;
        align-items: flex-end;
    }

    .popup-container {
        width: 100%;
        min-height: 70vh;
        background: #ffffff;
        border-radius: 0.5rem 0.5rem 0 0;
        box-sizing: border-box;
        padding-bottom: 0.4rem;
    }

    .popup-close {
        height: 1.04rem;
        padding-right: 0.4rem;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    .close-icon {
        width: 0.48rem;
        height: 0.48rem;
        background-size: 100% 100%;
        cursor: pointer;
    }

    .popup-title {
        padding: 0 0.4rem 0.32rem;
        font-family: DiDiSans-Pro-Bold;
        font-size: 0.48rem;
        line-height: 0.64rem;
        color: #0b0b0d;
    }

    .popup-description {
        padding: 0 0.4rem 0.4rem;
        font-size: 0.32rem;
        color: #0b0b0d;
    }

    .phone-input-area {
        margin: 0 0.4rem;
        height: 1rem;
        display: flex;
        align-items: center;
        border-bottom: 0.02rem solid #0b0b0d;

        &.is-input-error {
            border-bottom-color: #f2183d;
        }
    }

    .country-flag {
        width: 0.62rem;
        height: 0.38rem;
        background-size: 100% 100%;
        flex-shrink: 0;
    }

    .calling-code {
        margin: 0 0.21rem 0 0.11rem;
        font-family: DiDiSans-Pro-Bold;
        font-size: 0.4rem;
        color: #0b0b0d;
    }

    .phone-input {
        flex: 1;
        border: none;
        outline: none;
        background: none;
        font-family: DiDiSans-Pro-Bold;
        font-size: 0.4rem;
        color: #0c0c0d;
    }

    .tips-message {
        margin: 0.22rem 0.4rem 0;
        font-size: 0.24rem;
        color: #f2183d;
    }

    .confirm-btn {
        margin: 0.4rem;
        height: 1.04rem;
        border-radius: 0.32rem;
        background: #3355ff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: DiDiSans-Pro-ExtraBold;
        font-size: 0.4rem;
        color: #ffffff;
        cursor: pointer;

        &.disabled {
            background: #f2f2f5;
            color: #bababf;
            pointer-events: none;
        }
    }
</style>
